import { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Building2, CreditCard, Settings, Menu, Megaphone,
  ShieldCheck, ChevronDown, ChevronUp, Mail, Target, LifeBuoy, TrendingUp, Puzzle,
  FileText, CalendarClock, MessageSquare, Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SuperAdminUserMenu } from './SuperAdminUserMenu';
import { SuperAdminNotificationsBell } from './SuperAdminNotificationsBell';
import { SuperAdminRemindersBell } from './SuperAdminRemindersBell';
import { useSuperAdminAuth, saApi } from '@/contexts/SuperAdminAuthContext';

interface NavChild { label: string; href: string; module?: string; }
interface NavItem {
  label: string;
  icon: React.ElementType;
  href?: string;
  module?: string;          // PlatformRole permission module — view=false hides the item
  children?: NavChild[];
}

const NAV: NavItem[] = [
  { label: 'Dashboard',     icon: LayoutDashboard, href: '/super-admin/dashboard',     module: 'Dashboard' },
  { label: 'Prospects',     icon: Target,           href: '/super-admin/prospects',     module: 'Prospects' },
  { label: 'AI Assistant',  icon: Sparkles,         href: '/super-admin/ai-assistant' },
  { label: 'Demos',         icon: CalendarClock,    href: '/super-admin/demos' },
  { label: 'Organizations', icon: Building2,        href: '/super-admin/organizations', module: 'Organizations' },
  { label: 'Plans',         icon: CreditCard,       href: '/super-admin/plans',         module: 'Plans' },
  { label: 'Revenue',       icon: TrendingUp,       href: '/super-admin/revenue',       module: 'Plans' },
  {
    label: 'Administrator', icon: ShieldCheck, module: 'Administrator',
    children: [
      { label: 'Module', href: '/super-admin/modules', module: 'Administrator' },
      { label: 'Roles',  href: '/super-admin/roles',   module: 'Administrator' },
      { label: 'Users',  href: '/super-admin/users',   module: 'Administrator' },
    ],
  },
  { label: 'Campaigns',       icon: Megaphone, href: '/super-admin/campaigns',       module: 'Campaigns' },
  {
    // Templates group — houses every messaging template surface so
    // super-admin has one entry for all default content, not one item
    // per channel. Vendors have the same grouping.
    label: 'Templates', icon: FileText, module: 'Email Templates',
    children: [
      { label: 'Email',    href: '/super-admin/email-templates',    module: 'Email Templates' },
      { label: 'SMS',      href: '/super-admin/sms-templates',      module: 'Email Templates' },
      { label: 'WhatsApp', href: '/super-admin/whatsapp-templates', module: 'Email Templates' },
    ],
  },
  { label: 'Support Tickets', icon: LifeBuoy,  href: '/super-admin/tickets',         module: 'Support Tickets' },
  { label: 'Feedback',        icon: MessageSquare, href: '/super-admin/feedback' },
  { label: 'Settings',        icon: Settings,  href: '/super-admin/settings',        module: 'Settings' },
];

interface Props { children: ReactNode; title?: string; }

const DESKTOP_BP = 768;

export function SuperAdminLayout({ children, title }: Props) {
  const location = useLocation();
  const { permissions, isSuper, permissionsLoaded } = useSuperAdminAuth();
  const [collapsed, setCollapsed] = useState(false);       // desktop narrow mode
  const [mobileOpen, setMobileOpen] = useState(false);      // mobile drawer

  // DB-driven extension: any platform-scope module added in the Modules
  // catalog that isn't one of the built-in NAV items above is appended here,
  // so a new super-admin module shows in the sidebar (permission-gated via
  // canSee) without a code change. Built-in items keep their hand-tuned icons.
  const [extraNav, setExtraNav] = useState<NavItem[]>([]);
  useEffect(() => {
    saApi.get('/admin/modules', { params: { scope: 'platform', type: 'M' } })
      .then(({ data }) => {
        const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);
        const known = new Set(
          NAV.flatMap(n => [n.href, ...(n.children?.map(c => c.href) ?? [])]).filter(Boolean) as string[],
        );
        const extra: NavItem[] = rows
          .filter(m => m?.isActive !== false && m?.pageLink && m.pageLink !== '#' && !known.has(m.pageLink))
          .map(m => ({ label: m.name, icon: Puzzle, href: m.pageLink, module: m.name }));
        setExtraNav(extra);
      })
      .catch(() => setExtraNav([]));
  }, []);

  // Visibility rule:
  //   1. Super Admin role           → see everything (preset is full-access).
  //   2. permissions not loaded yet → render nothing (skeleton) so the user
  //      never sees a flash of menu items they will lose half a second later.
  //   3. item has no module         → always visible (admin-group children
  //      ride on the parent module field).
  //   4. item.module + perms        → permissions[module].view !== false
  //      (treats absent keys as true so newly-added modules do not silently
  //      hide existing menu items).
  const canSee = (item: { module?: string }) => {
    if (isSuper) return true;
    if (!permissionsLoaded) return false;
    if (!item.module) return true;
    // Default-deny: a sub-admin sees a module only if their role explicitly
    // grants view=true (an unmapped module stays hidden), not "unless denied".
    return permissions?.[item.module]?.view === true;
  };

  // Standalone items pass via canSee; group items (no href) appear when
  // at least one of their children is visible — so a group is reachable
  // even if its parent module is off but a child module is allowed.
  const VISIBLE_NAV = [...NAV, ...extraNav]
    .map(item => item.children
      ? { ...item, children: item.children.filter(canSee) }
      : item,
    )
    .filter(item => {
      if (!item.children) return canSee(item);
      return item.children.length > 0;
    });

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open (iOS)
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const onHamburger = () => {
    if (window.innerWidth < DESKTOP_BP) setMobileOpen(p => !p);
    else setCollapsed(p => !p);
  };

  const getDefaultOpen = (): Record<string, boolean> => {
    const open: Record<string, boolean> = {};
    VISIBLE_NAV.forEach(item => {
      if (item.children) {
        const active = item.children.some(c => location.pathname === c.href);
        if (active) open[item.label] = true;
      }
    });
    return open;
  };

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(getDefaultOpen);
  const toggleGroup = (label: string) =>
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));

  const isActive = (href: string) => location.pathname === href;
  const isGroupActive = (item: NavItem) => item.children?.some(c => isActive(c.href)) ?? false;

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: '#F8F9FA' }}>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — inline on md+, fixed drawer on mobile. Transform-driven
          open/close keeps it silky smooth and GPU-accelerated.
          data-app-sidebar: side drawers open up to this edge. */}
      <div data-app-sidebar className={cn(
        'flex h-full flex-col bg-white border-r border-gray-100 flex-shrink-0 overflow-hidden',
        // Desktop: static, width transitions
        'md:static md:translate-x-0 md:transition-[width] md:duration-300',
        collapsed ? 'md:w-[60px]' : 'md:w-60',
        // Mobile: fixed drawer, transform transitions
        'fixed inset-y-0 left-0 z-40 w-64 transition-transform duration-300',
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
      )}>
        {/* Brand */}
        <div className="flex h-16 items-center border-b border-gray-100 px-4 flex-shrink-0">
          {collapsed ? (
            <span className="text-[#6366F1] font-bold text-lg mx-auto">A</span>
          ) : (
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#6366F1] tracking-tight leading-none">BrixLead</span>
              <span className="mt-1 inline-flex items-center self-start px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-[#6366F1] leading-none">
                Super Admin
              </span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="no-scrollbar flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {VISIBLE_NAV.map(item => {
            if (item.children && item.children.length > 0) {
              const groupActive = isGroupActive(item);
              const isOpen = !collapsed && (openGroups[item.label] ?? false);
              return (
                <div key={item.label}>
                  <button
                    onClick={() => !collapsed && toggleGroup(item.label)}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      'flex items-center w-full rounded-lg px-3 py-2 text-sm transition-colors',
                      collapsed ? 'justify-center' : 'justify-between gap-3',
                      groupActive
                        ? 'bg-[#6366F1] text-white'
                        : 'text-[#64748B] hover:bg-gray-50 hover:text-[#1E293B]',
                    )}
                  >
                    <div className={cn('flex items-center', collapsed ? '' : 'gap-3')}>
                      <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.75} />
                      {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                    </div>
                    {!collapsed && (
                      isOpen
                        ? <ChevronUp className="h-4 w-4 flex-shrink-0" />
                        : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-0.5 mb-1 border-l-2 border-gray-100 pl-3 space-y-0.5">
                      {item.children.map(child => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className={cn(
                            'block rounded-md px-3 py-1.5 text-sm transition-colors whitespace-nowrap',
                            isActive(child.href)
                              ? 'text-[#6366F1] bg-indigo-50'
                              : 'text-[#64748B] hover:text-[#1E293B] hover:bg-gray-50',
                          )}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            const active = isActive(item.href!);
            return (
              <Link
                key={item.href}
                to={item.href!}
                title={collapsed ? item.label : undefined}
                className={cn(
                  'flex items-center rounded-lg px-3 py-2 text-sm transition-colors',
                  collapsed ? 'justify-center' : 'gap-3',
                  active
                    ? 'bg-[#6366F1] text-white'
                    : 'text-[#64748B] hover:bg-gray-50 hover:text-[#1E293B]',
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.75} />
                {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

      </div>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-14 sm:h-16 items-center justify-between bg-white border-b border-gray-100 px-3 sm:px-6 flex-shrink-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button
              onClick={onHamburger}
              className="text-[#64748B] hover:text-[#6366F1] transition-colors p-1 flex-shrink-0"
              aria-label="Toggle navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            {title && <h1 className="text-base sm:text-xl font-bold text-[#6366F1] truncate">{title}</h1>}
          </div>
          <div className="flex items-center gap-3">
            <SuperAdminNotificationsBell />
            <SuperAdminRemindersBell />
            <SuperAdminUserMenu />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-3 sm:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
